import React, { useState, useEffect } from 'react';
import { Table, Spin, Tag, Button, Space, Descriptions, Modal, Divider, Tabs } from 'antd';
import { UnorderedListOutlined, AuditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../../Models/Utilisateur';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';

/**
 * Restructuration — Suivi de la période probatoire COBAC (art. 14) :
 * reclassement possible si 180 jours (paramétrable) sans impayé
 * OU remboursement fonds propres >= max(txFondsPro% créance, int. arriérés).
 */
const SuiviProbatoire = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();

    const [loading, setLoading] = useState(false);
    const [cdEtat, setCdEtat] = useState<string>('EN COURS');
    const [rows, setRows] = useState<any[]>([]);
    const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
    const [detail, setDetail] = useState<any>(null);   // {dossier, scenarios, docs, probation, echeances}
    const [docs, setDocs] = useState<any[]>([]);

    const fmtD = (v: any) => v ? new Date(String(v).substring(0, 10)).toLocaleDateString('fr-FR') : '—';

    useEffect(() => { load(); }, [cdEtat]);

    function callApi(action: string, extra: any = {}, onSuccess?: (res: any) => void, silent = false) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.RestructurePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action, utiVal: user.coduti, codAge: user.codage, ...extra
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    if (onSuccess) onSuccess(res);
                    else if (!silent) SweetAlert.fire({ title: res.Message, icon: "success" });
                } else if (!silent) {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    function load() {
        callApi('PROBATOIRE', { cdEtat }, (res) => {
            setRows(res.Result?.Table ?? []);
            setSelectedKey(null); setDetail(null);
        }, true);
    }

    // Charge le détail complet du dossier de restructuration
    function loadDetail(row: any) {
        callApi('DETAIL', { idRestru: row.IDRESTRU, numDos: row.NUMDOS }, (res) => {
            const t = res.Result ?? {};
            const tables = Object.values(t).filter(Array.isArray) as any[][];
            setDetail({
                dossier: tables[0]?.[0] ?? null,
                scenarios: tables[1] ?? [],
                docs: tables[2] ?? [],
                probation: tables[3] ?? [],
                echeances: tables[4] ?? []
            });
            setDocs(tables[2] ?? []);
        }, true);
    }

    // ---------------- Pièces : téléchargement + visualisation ----------------
    const [viewer, setViewer] = useState<{ open: boolean; url: string; mime: string; name: string }>(
        { open: false, url: '', mime: '', name: '' });

    // base64 -> Blob (détecte PDF / PNG / JPEG via les magic bytes)
    function base64ToBlob(b64: string): { blob: Blob; mime: string } {
        const bin = atob(b64.replace(/\s/g, ''));
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        let mime = 'application/octet-stream';
        if (bin.startsWith('%PDF')) mime = 'application/pdf';
        else if (bytes[0] === 0x89 && bytes[1] === 0x50) mime = 'image/png';
        else if (bytes[0] === 0xFF && bytes[1] === 0xD8) mime = 'image/jpeg';
        return { blob: new Blob([bytes], { type: mime }), mime };
    }

    function fetchDoc(idDoc: number, name: string, mode: 'download' | 'view') {
        callApi('GETDOC', { idScen: idDoc }, (res) => {
            const d = res.Result?.Table?.[0];
            if (!d?.PIECE) { SweetAlert.fire({ title: "Document vide", icon: "warning" }); return; }
            const { blob, mime } = base64ToBlob(d.PIECE);
            const url = URL.createObjectURL(blob);
            if (mode === 'download') {
                const a = document.createElement('a');
                a.href = url;
                a.download = name ?? d.LIBDOC ?? `doc_${idDoc}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 4000);
            } else {
                setViewer({ open: true, url, mime, name: name ?? d.LIBDOC ?? `doc_${idDoc}` });
            }
        }, true);
    }

    const sel: any = rows.find(r => r.Id === selectedKey);
    const dos: any = detail?.dossier;

    const colsScen: ColumnsType<any> = [
        { title: 'Id', dataIndex: 'Id', width: 50 },
        { title: 'Scénario', dataIndex: 'LIBSCEN' },
        { title: 'Nb éch.', dataIndex: 'NBRECHE', width: 70 },
        { title: 'Taux', dataIndex: 'TAUXINT', width: 70, render: (v: any) => v != null ? `${v}%` : '—' },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', render: (v: number) => formatNumber(v) },
        { title: 'Frais', dataIndex: 'MTNFRAIS', width: 80, render: (v: number) => formatNumber(v ?? 0) },
        { title: 'Retenu', dataIndex: 'ISCHOISI', width: 75, render: (v: any) => (v === 1 || v === true) ? <Tag color="green">Oui</Tag> : '' },
    ];

    const colsEch: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'numSeq', width: 55 },
        { title: 'Date', dataIndex: 'dateEcheacnce', width: 105 },
        { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
        { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
    ];

    const cols: ColumnsType<any> = [
        { title: 'Dossier', dataIndex: 'NUMDOS', width: 110 },
        { title: 'Client', dataIndex: 'NOMCLI' },
        { title: 'Créance arrêtée', dataIndex: 'CREANCE', render: (v: number) => formatNumber(v ?? 0) },
        { title: 'Int. arriérés', dataIndex: 'INTARRIERES', width: 110, render: (v: number) => formatNumber(v ?? 0) },
        { title: 'Min fonds propres', dataIndex: 'MNTMINFONDSPRO', width: 130, render: (v: number) => formatNumber(v ?? 0) },
        { title: 'Début', dataIndex: 'DATDEBUT', width: 105, render: (v: any) => v ? String(v).substring(0, 10) : '' },
        { title: 'Fin prévue', dataIndex: 'DATFINPREV', width: 105, render: (v: any) => v ? String(v).substring(0, 10) : '' },
        {
            title: 'Impayé', dataIndex: 'HASIMPAYE', width: 80,
            render: (v: any) => v === 1 ? <Tag color="red">Oui</Tag> : <Tag color="green">Non</Tag>
        },
        {
            title: 'État', dataIndex: 'CDETAT', width: 105,
            render: (v: string) => <Tag color={v === 'CONFORME' ? 'green' : v === 'DEFAILLANT' ? 'red' : 'gold'}>{v}</Tag>
        },
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>Suivi probatoire — restructuration</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <div className='bG'>
                    <legend><Typography color={"#116599"}>Actions</Typography></legend>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button size='large' className='bt_add' onClick={load} type="ghost" htmlType="button" block>Actualiser</Button>
                        <Divider plain style={{ margin: '4px 0', borderColor: '#116599' }}>Verdict COBAC</Divider>
                        <Button size='large' className='bt_add' disabled={!sel || sel.CDETAT !== 'EN COURS'} onClick={() => {
                            SweetAlert.fire({
                                title: `Reclasser le prêt ${sel?.NUMDOS} ?`,
                                text: "La créance redevient saine après la période probatoire conforme (ou fonds propres suffisants).",
                                showCancelButton: true, confirmButtonText: "Reclasser", cancelButtonText: "Non",
                                confirmButtonColor: "#116599", icon: 'question'
                            }).then((r) => {
                                if (r.isConfirmed)
                                    callApi('RECLASSER', { idRestru: sel?.IDRESTRU, numDos: sel?.NUMDOS },
                                        (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); load(); });
                            });
                        }} type="ghost" htmlType="button" block>Reclasser (encours sain)</Button>
                        <Button size='large' className='bt_cancel' style={{ color: '#fff' }} disabled={!sel || sel.CDETAT !== 'EN COURS'} onClick={() => {
                            SweetAlert.fire({
                                title: `Déclasser le prêt ${sel?.NUMDOS} ?`,
                                input: 'text', inputPlaceholder: 'Motif (ex. impayé pendant la probation)',
                                showCancelButton: true, confirmButtonText: "Déclasser", cancelButtonText: "Non",
                                confirmButtonColor: "#a41b1b", icon: 'warning'
                            }).then((r) => {
                                if (r.isConfirmed)
                                    callApi('DECLASSER', { idRestru: sel?.IDRESTRU, numDos: sel?.NUMDOS, coment: r.value },
                                        (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); load(); });
                            });
                        }} type="ghost" htmlType="button" block>Déclasser</Button>
                    </Space>
                    {sel && (
                        <Tag color={sel.CDETAT === 'CONFORME' ? 'green' : sel.CDETAT === 'DEFAILLANT' ? 'red' : 'gold'}
                            style={{ marginTop: 10, whiteSpace: 'normal' }}>
                            {sel.NUMDOS} — {sel.CDETAT}
                        </Tag>
                    )}
                </div>
                <div className='clientP'>
                    <div style={{ width: '88%' }}>
                        <Tabs size='small' type="card" defaultActiveKey="1" tabBarGutter={8}
                            tabBarStyle={{ overflowX: 'auto', whiteSpace: 'nowrap' }} items={[
                                {
                                    key: '1',
                                    label: <span><UnorderedListOutlined /> Périodes probatoires</span>,
                                    children: (
                                        <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                                            <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                                <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>
                                                    Périodes probatoires
                                                    <Space style={{ marginLeft: 12 }}>
                                                        {['EN COURS', 'CONFORME', 'DEFAILLANT'].map(s => (
                                                            <Tag key={s} color={cdEtat === s ? '#116599' : 'default'} style={{ cursor: 'pointer' }}
                                                                onClick={() => setCdEtat(s)}>{s}</Tag>
                                                        ))}
                                                    </Space>
                                                </legend>
                                                <Table size='small' columns={cols} dataSource={rows}
                                                    scroll={{ x: 900, y: 380 }} pagination={false} rowKey="Id"
                                                    rowSelection={{
                                                        type: 'radio', selectedRowKeys: selectedKey !== null ? [selectedKey] : [],
                                                        onChange: (keys) => {
                                                            const k = keys[0] ?? null; setSelectedKey(k);
                                                            const row = rows.find(r => r.Id === k);
                                                            if (row) loadDetail(row); else setDetail(null);
                                                        }
                                                    }}
                                                    onRow={(r) => ({ onClick: () => { setSelectedKey(r.Id); loadDetail(r); }, style: { cursor: 'pointer' } })} />
                                            </fieldset>
                                        </div>
                                    )
                                },
                                {
                                    key: '2',
                                    label: <span><AuditOutlined /> Détail du dossier</span>,
                                    children: (
                                        <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                        {detail && dos ? (
                            <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Détail du dossier {dos.NUMDOS}</legend>

                                {/* Dossier + probation */}
                                <Descriptions size="small" column={2} bordered style={{ marginBottom: 10 }}>
                                    <Descriptions.Item label="Client">{`${dos.NOMCLI ?? ''} (${dos.NUMCLI ?? ''})`}</Descriptions.Item>
                                    <Descriptions.Item label="Type de prêt">{dos.TYPEPRET}</Descriptions.Item>
                                    <Descriptions.Item label="Montant initial">{formatNumber(dos.mntOrig)}</Descriptions.Item>
                                    <Descriptions.Item label="Statut dossier"><Tag color="gold">{dos.CDETAT}</Tag></Descriptions.Item>
                                    <Descriptions.Item label="Motif">{dos.MOTIF ?? '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Diagnostic">{dos.DIAGNOSTIC ?? '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Début probation">{fmtD(sel?.DATDEBUT)}</Descriptions.Item>
                                    <Descriptions.Item label="Fin prévue">{fmtD(sel?.DATFINPREV)}</Descriptions.Item>
                                    <Descriptions.Item label="Fonds propres min.">{formatNumber(sel?.MNTMINFONDSPRO ?? 0)}</Descriptions.Item>
                                    <Descriptions.Item label="Intérêts arriérés">{formatNumber(sel?.INTARRIERES ?? 0)}</Descriptions.Item>
                                </Descriptions>

                                {/* Scénarios */}
                                <Typography color="#116599" style={{ margin: '6px 0' }}><b>Scénarios négociés</b></Typography>
                                <Table size='small' columns={colsScen} dataSource={detail.scenarios}
                                    pagination={false} rowKey="Id" style={{ marginBottom: 10 }} />

                                {/* Échéancier du scénario retenu */}
                                {detail.echeances?.length > 0 && (
                                    <>
                                        <Typography color="#116599" style={{ margin: '6px 0' }}><b>Échéancier appliqué</b></Typography>
                                        <Table size='small' columns={colsEch} dataSource={detail.echeances}
                                            scroll={{ x: 700, y: 200 }} pagination={false} rowKey="numSeq" style={{ marginBottom: 10 }} />
                                    </>
                                )}

                                {/* Pièces jointes */}
                                <Typography color="#116599" style={{ margin: '6px 0' }}><b>Pièces jointes</b></Typography>
                                <Table size='small' dataSource={docs} pagination={false} rowKey="Id" style={{ marginBottom: 10 }}
                                    columns={[
                                        { title: 'Type', dataIndex: 'TYPDOC', width: 100, render: (v: string) => <Tag>{v}</Tag> },
                                        { title: 'Document', dataIndex: 'LIBDOC' },
                                        {
                                            title: 'Pièce', dataIndex: 'HASPIECE', width: 180,
                                            render: (v: any, d: any) => v === 1 || d.PIECE != null
                                                ? <Space size={4}>
                                                    <Button size='small' type='link' onClick={() => fetchDoc(d.Id, d.LIBDOC, 'view')}>Visualiser</Button>
                                                    <Button size='small' type='link' onClick={() => fetchDoc(d.Id, d.LIBDOC, 'download')}>Télécharger</Button>
                                                </Space>
                                                : <Tag color="orange">Non jointe</Tag>
                                        },
                                        { title: 'Déposé par', dataIndex: 'UTILIS', width: 90 },
                                        { title: 'Déposé le', dataIndex: 'DATCRE', width: 100, render: (v: any) => fmtD(v) },
                                    ]} />

                                {/* Intervenants */}
                                <Typography color="#116599" style={{ margin: '6px 0' }}><b>Intervenants sur le dossier</b></Typography>
                                <Descriptions size="small" column={2} bordered>
                                    <Descriptions.Item label="Dossier saisi par">{dos.UTILIS ?? '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Date de saisie">{fmtD(dos.DATCRE)}</Descriptions.Item>
                                    <Descriptions.Item label="Décision par (organe)">{dos.UTIVAL ?? '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Date de décision">{fmtD(dos.DATVAL)}</Descriptions.Item>
                                    <Descriptions.Item label="Avenant déposé par">{docs.find((d: any) => d.TYPDOC === 'AVENANT' && (d.HASPIECE === 1 || d.PIECE != null))?.UTILIS ?? '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Avenant déposé le">{fmtD(docs.find((d: any) => d.TYPDOC === 'AVENANT' && (d.HASPIECE === 1 || d.PIECE != null))?.DATCRE)}</Descriptions.Item>
                                </Descriptions>
                            </fieldset>
                        ) : (
                            <Tag color="orange" style={{ marginTop: 8 }}>Sélectionnez une période dans l'onglet « Périodes probatoires ».</Tag>
                        )}
                                        </div>
                                    )
                                }
                            ]} />
                    </div>
                </div>
            </Spin>
            {/* Visionneuse de pièce */}
            <Modal open={viewer.open} title={viewer.name} footer={null} width={900}
                onCancel={() => { URL.revokeObjectURL(viewer.url); setViewer({ open: false, url: '', mime: '', name: '' }); }}>
                {viewer.mime === 'application/pdf' ? (
                    <iframe src={viewer.url} title={viewer.name} style={{ width: '100%', height: '70vh', border: 'none' }} />
                ) : viewer.mime.startsWith('image/') ? (
                    <img src={viewer.url} alt={viewer.name} style={{ maxWidth: '100%' }} />
                ) : (
                    <p>Format non visualisable — utilisez « Télécharger ».</p>
                )}
            </Modal>
        </div>
    );
};

export default SuiviProbatoire;
